import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Skeleton,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useNavigate } from 'react-router-dom';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

const cellStyle = {
  padding: '10px 16px',
};

const statusColors = {
  Active: '#00AC48',
  Paused: '#FF7B00',
};

// Sample fallback data (simulating async fetch)
const sampleData = [
  {
    id: 1,
    name: 'Weekly Sales Report',
    email: 'sales@company.com',
    recurring: 'Weekly',
    next: '17 Jun, 2025',
    status: 'Active',
  },
  {
    id: 2,
    name: 'Monthly HR Summary',
    email: 'hr@company.com',
    recurring: 'Monthly',
    next: '01 Jul, 2025',
    status: 'Paused',
  },
  {
    id: 3,
    name: 'DevOps Logs',
    email: 'devops@company.com',
    recurring: 'Daily',
    next: '17 Jun, 2025',
    status: 'Active',
  },
];

// Custom Checkbox
const customCheckbox = (checked, onChange) => (
  <Checkbox
    checked={checked}
    onChange={onChange}
    icon={
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '4px',
          border: '1px solid #D0D5DD',
          backgroundColor: '#E5E8EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M20 6L9 17L4 12"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    }
    checkedIcon={
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '4px',
          backgroundColor: '#1976d2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M20 6L9 17L4 12"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    }
  />
);

const ScheduledReportsTable = () => {
  const [rows, setRows] = useState(null);
  const [anchorEls, setAnchorEls] = useState({});
  const [search, setSearch] = useState('');
  const [checkedRows, setCheckedRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    setTimeout(() => {
      setRows(sampleData);
    }, 2000);
  }, []);

  const handleClick = (event, id) => {
    setAnchorEls(prev => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleClose = (id) => {
    setAnchorEls(prev => ({ ...prev, [id]: null }));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setCheckedRows(rows.map(row => row.id));
    } else {
      setCheckedRows([]);
    }
  };

  const handleSelectOne = (id) => {
    setCheckedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const filteredRows = rows?.filter(row =>
    row.name.toLowerCase().includes(search.toLowerCase())
  );

  const allChecked = rows && checkedRows.length === rows.length;

  return (
    <Box>
      {/* <Box><Button onClick={() => navigate('/reports')} style={{ color: '#1A1F36', fontSize: '24px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: 2, marginBottom: '24px' }}><KeyboardArrowLeftIcon sx={{ color: '#1760EC', width: '30px', height: '30px', }} /> Reports</Button></Box> */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="600" sx={{fontSize: {xl: '20px' , lg: '16px' , md: '16px' , sm: '16px' , xs: '16px'}}}>
          Scheduled Reports
        </Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: '300px',
            '& .MuiOutlinedInput-root': {
              background: '#fff',
              border: '1px solid #E5E8EB',
              borderRadius: '60px',
              paddingRight: '8px',
              fontSize: '14px',
            },
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 'none', background: 'transparent' }}>
        <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}>
          <TableHead sx={{ backgroundColor: '#DFDFDF' }}>
            <TableRow>
              <TableCell sx={{ ...cellStyle, border: 'none', minWidth: '32px', maxWidth: '32px', borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                {customCheckbox(allChecked, handleSelectAll)}
              </TableCell>
              <TableCell sx={{ ...cellStyle, border: 'none', }}>
                #
              </TableCell>
              <TableCell sx={{ ...cellStyle, border: 'none', color: '#626262' }}>Report Name</TableCell>
              <TableCell sx={{ ...cellStyle, border: 'none', color: '#626262' }}>Report Scheduled Send to</TableCell>
              <TableCell sx={{ ...cellStyle, border: 'none', color: '#626262' }}>Recurring</TableCell>
              <TableCell sx={{ ...cellStyle, border: 'none', color: '#626262', textAlign: 'center' }}>Status</TableCell>
              <TableCell
                sx={{
                  ...cellStyle,
                  border: 'none',
                  borderTopRightRadius: '10px',
                  borderBottomRightRadius: '10px',
                  textAlign: 'center',
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!rows
              ? [...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', minWidth: '32px', maxWidth: '32px', borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                    <Skeleton variant="rectangular" width={24} height={24} />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', }}>
                    <Skeleton width={10} />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff' }}>
                    <Skeleton width={120} />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff' }}>
                    <Skeleton width={150} />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff' }}>
                    <Skeleton width={80} />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', textAlign: 'center' }}>
                    <Skeleton width={60} height={24} />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', borderTopRightRadius: '10px', borderBottomRightRadius: '10px', textAlign: 'center' }}>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                </TableRow>
              ))
              : filteredRows.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', minWidth: '32px', maxWidth: '32px', borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                    {customCheckbox(checkedRows.includes(row.id), () => handleSelectOne(row.id))}
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff' }}>
                    <Button variant="text" sx={{ textTransform: 'none', color: '#004DFF', fontWeight: 500 }}>
                      {row.name}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff' }}>{row.email}</TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff' }}>
                    {row.recurring}
                    <Box sx={{ fontSize: '12px', color: '#888' }}>Next: {row.next}</Box>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', textAlign: 'center' }}>
                    <Chip
                      label={row.status}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: statusColors[row.status],
                        color: statusColors[row.status],
                        fontWeight: 500,
                        px: 1.5,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, backgroundColor: '#fff', borderTopRightRadius: '10px', borderBottomRightRadius: '10px', textAlign: 'center' }}>
                    <IconButton
                      onClick={(e) => handleClick(e, row.id)}
                      sx={{
                        border: '1px solid #0076FE',
                        color: '#0076FE',
                        fontSize: '14px',
                        borderRadius: '30px',
                        padding: '3px 16px',
                        fontWeight: '500',
                      }}
                    >
                      Action <KeyboardArrowDownIcon sx={{ marginLeft: '10px' }} />
                    </IconButton>
                    <Menu anchorEl={anchorEls[row.id]} open={Boolean(anchorEls[row.id])} onClose={() => handleClose(row.id)} sx={{ borderRadius: '12px' }}>
                      <MenuItem sx={{ fontSize: '14px', color: '#1A1F36' }} onClick={() => handleClose(row.id)}>Edit</MenuItem>
                      <MenuItem sx={{ fontSize: '14px', color: '#1A1F36' }} onClick={() => handleClose(row.id)}>Resume</MenuItem>
                      <MenuItem sx={{ fontSize: '14px', color: '#1A1F36' }} onClick={() => handleClose(row.id)}>Delete</MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ScheduledReportsTable;
